import type { CookiePreferences } from '@/shared/types/CookiePreferences'

const storageKey = 'academy-cookie-preferences-v1'
const changedEvent = 'academy-cookie-preferences-changed'
const openEvent = 'academy-cookie-preferences-open'
let memoryValue: CookiePreferences | null = null

function parseCookiePreferences(value: string | null): CookiePreferences | null {
  if (!value) return null

  try {
    const parsed: unknown = JSON.parse(value)

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'functional' in parsed &&
      typeof parsed.functional === 'boolean' &&
      'analytics' in parsed &&
      typeof parsed.analytics === 'boolean' &&
      'savedAt' in parsed &&
      typeof parsed.savedAt === 'string'
    ) {
      return {
        functional: parsed.functional,
        analytics: parsed.analytics,
        savedAt: parsed.savedAt
      }
    }
  } catch {
    return null
  }

  return null
}

export function readCookiePreferences(): CookiePreferences | null {
  try {
    return parseCookiePreferences(window.localStorage.getItem(storageKey)) ?? memoryValue
  } catch {
    return memoryValue
  }
}

export function saveCookiePreferences(functional: boolean, analytics: boolean): CookiePreferences {
  const value = { functional, analytics, savedAt: new Date().toISOString() }
  memoryValue = value

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value))
  } catch {
    memoryValue = value
  }

  window.dispatchEvent(new Event(changedEvent))
  return value
}

export function openCookiePreferences() {
  window.dispatchEvent(new Event(openEvent))
}

export function subscribeToCookiePreferences(listener: (value: CookiePreferences) => void) {
  const handleChange = () => {
    const value = readCookiePreferences()
    if (value) listener(value)
  }

  window.addEventListener(changedEvent, handleChange)
  return () => window.removeEventListener(changedEvent, handleChange)
}

export function subscribeToCookiePreferencesOpen(listener: () => void) {
  window.addEventListener(openEvent, listener)
  return () => window.removeEventListener(openEvent, listener)
}
